var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, isValidElement, useLayoutEffect, useRef, useState } from 'react';
var EDGE_PADDING = 8;
var RECHARTS_DEFAULT_OFFSET = 10;
var ESTIMATED_TOOLTIP_WIDTH = 300;
function isRightmostBarGroup(coordinateX, viewBoxX, viewBoxWidth, barGroupCount) {
    var count = Math.max(barGroupCount, 1);
    if (count <= 1)
        return true;
    var slotWidth = viewBoxWidth / count;
    var relativeCenter = coordinateX - viewBoxX;
    var barIndex = Math.round((relativeCenter - slotWidth / 2) / slotWidth);
    return barIndex >= count - 1;
}
function getRightEdgeShiftX(slotWidth) {
    return slotWidth / 2 + EDGE_PADDING - RECHARTS_DEFAULT_OFFSET;
}
function getLeftOfSlotTransform(slotWidth) {
    var offset = -slotWidth / 2 - EDGE_PADDING - RECHARTS_DEFAULT_OFFSET;
    return "translateX(calc(-100% + ".concat(offset, "px))");
}
function getClampedLeftOfBarShiftX(coordinateX, viewBoxX, viewBoxWidth, tooltipWidth) {
    var anchorRight = coordinateX - EDGE_PADDING;
    var minLeft = viewBoxX + EDGE_PADDING;
    var maxLeft = viewBoxX + viewBoxWidth - tooltipWidth - EDGE_PADDING;
    var targetLeft = Math.min(Math.max(anchorRight - tooltipWidth, minLeft), Math.max(minLeft, maxLeft));
    return targetLeft - (coordinateX + RECHARTS_DEFAULT_OFFSET);
}
function ClampedLeftOfBarTooltip(_a) {
    var coordinateX = _a.coordinateX, viewBoxX = _a.viewBoxX, viewBoxWidth = _a.viewBoxWidth, children = _a.children;
    var ref = useRef(null);
    var _b = useState(function () {
        return getClampedLeftOfBarShiftX(coordinateX, viewBoxX, viewBoxWidth, ESTIMATED_TOOLTIP_WIDTH);
    }), shiftX = _b[0], setShiftX = _b[1];
    useLayoutEffect(function () {
        var _a;
        var width = ((_a = ref.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || ESTIMATED_TOOLTIP_WIDTH;
        setShiftX(getClampedLeftOfBarShiftX(coordinateX, viewBoxX, viewBoxWidth, width));
    }, [coordinateX, viewBoxX, viewBoxWidth]);
    return (_jsx("div", __assign({ ref: ref, style: { transform: "translateX(".concat(shiftX, "px)") } }, { children: children })));
}
export function createEdgePositionedBarTooltipContent(content, barGroupCount) {
    return function (props) {
        var _a, _b, _c;
        var active = props.active, coordinate = props.coordinate, viewBox = props.viewBox, payload = props.payload, label = props.label;
        var tooltipContent = typeof content === 'function'
            ? content(props)
            : isValidElement(content)
                ? cloneElement(content, { active: active, payload: payload, label: label })
                : null;
        if (!active || !coordinate || !viewBox) {
            return tooltipContent;
        }
        var coordinateX = (_a = coordinate.x) !== null && _a !== void 0 ? _a : 0;
        var viewBoxX = (_b = viewBox.x) !== null && _b !== void 0 ? _b : 0;
        var viewBoxWidth = (_c = viewBox.width) !== null && _c !== void 0 ? _c : 0;
        var count = Math.max(barGroupCount, 1);
        var slotWidth = viewBoxWidth / count;
        var preferLeft = isRightmostBarGroup(coordinateX, viewBoxX, viewBoxWidth, count);
        if (!preferLeft) {
            return (_jsx("div", __assign({ style: {
                    transform: "translateX(".concat(getRightEdgeShiftX(slotWidth), "px)")
                } }, { children: tooltipContent })));
        }
        if (count > 1) {
            return (_jsx("div", __assign({ style: { transform: getLeftOfSlotTransform(slotWidth) } }, { children: tooltipContent })));
        }
        return (_jsx(ClampedLeftOfBarTooltip, __assign({ coordinateX: coordinateX, viewBoxX: viewBoxX, viewBoxWidth: viewBoxWidth }, { children: tooltipContent })));
    };
}
